import { useRouteContext } from "@tanstack/react-router";
import { Controller, UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import DateInput from "@/components/shared/forms/DateInput";
import SearchComboBox, {
  type ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { type CLEN } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { useCLENFormLogic } from "./useCLENFormLogic";
import { FaChalkboardTeacher } from "react-icons/fa";

export default function CLENAddEditForm({
  selectedCLEN,
  form,
  clenDefaults,
  classroomOptions = [],
}: {
  selectedCLEN: CLEN | undefined;
  form: UseFormReturn<CLEN, any, CLEN>;
  clenDefaults: Partial<CLEN> | undefined;
  classroomOptions?: ComboBoxOption[];
}) {
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("CLENEdit");
  const tooltipMap = leaContext.coreData.tooltipMap.get("CLEN");

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const { watchedFields, validators } = useCLENFormLogic(control);

  const {
    watchPreschoolCode,
    watchProgramYear,
    watchLocalClassroomId,
    watchCSPPID,
    watchLocalChildId,
  } = watchedFields;

  const { validateEnrollmentStart, validateEnrollmentEnd } = validators;

  const isClassroomFieldDisabled = viewOnly || !!selectedCLEN?.LocalClassroomId;

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(watchProgramYear)
    .map((x) => {
      return {
        Text: x.PreschoolName,
        Value: x.PreschoolCDSCode,
      };
    });

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div
        className="form-card-modern mb-3"
        style={!isClassroomFieldDisabled ? { paddingBottom: 180 } : undefined}
      >
        <div className="section-header">
          <FaChalkboardTeacher className="section-icon" />
          <h5 className="section-title">Classroom Enrollment</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedCLEN?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    value={watchProgramYear}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedCLEN?.PreschoolCode && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    value={watchPreschoolCode}
                    {...register(name, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  maxLength={50}
                  value={watchCSPPID}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  maxLength={50}
                  value={watchLocalChildId}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="LocalClassroomId"
              labelText="Classroom ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                isClassroomFieldDisabled ? (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    readOnly
                    maxLength={50}
                    value={watchLocalClassroomId}
                    {...register(name, {
                      required: "Classroom ID is required",
                    })}
                  />
                ) : (
                  <Controller
                    name="LocalClassroomId"
                    control={control}
                    rules={{ required: "Classroom ID is required" }}
                    render={({ field }) => (
                      <SearchComboBox
                        id={id}
                        options={classroomOptions}
                        value={field.value}
                        placeholder="Search by classroom name or ID"
                        noMatchMessage="No matching classrooms found"
                        hasError={!!errors.LocalClassroomId}
                        isRequired
                        ariaDescribedBy={`${id}-description`}
                        onChange={(value) => {
                          setValue("LocalClassroomId", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    )}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ClassroomEnrollmentStart"
              labelText="Classroom Enrollment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              tooltip={tooltipMap?.get("ClassroomStart")}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedCLEN && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{
                      required: "Classroom Enrollment Start Date is required",
                      validate: validateEnrollmentStart,
                    }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    max="9999-12-31"
                    {...register(name, {
                      required: "Classroom Enrollment Start Date is required",
                      validate: validateEnrollmentStart,
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ClassroomEnrollmentEnd"
              labelText="Classroom Enrollment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              tooltip={tooltipMap?.get("ClassroomEnd")}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, {
                    validate: validateEnrollmentEnd,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <span
          id="spnAddOrEditClassroomErrorOverall"
          className="row ps-3 mt-3 pe-3 float-end"
          style={{ color: "#CC0000" }}
        />
      </div>
    </Container>
  );
}
